import { session } from "@/lib/auth";
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

interface AuthValue {
  email: string | null;
  /**
   * 세션은 `sessionStorage` 에서 동기적으로 읽히므로 실제로 기다릴 게 없어요.
   * 외부 IdP 를 쓸 때는 초기 상태를 비동기로 받아야 해서 이 플래그가 필요했는데,
   * 지금은 항상 `false` 예요. 라우터가 이 값으로 분기하고 있어서 남겨 뒀어요.
   */
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (input: { accessToken: string; expiresIn: number; email: string }) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState(() => session.get());

  const signIn = useCallback((input: { accessToken: string; expiresIn: number; email: string }) => {
    setCurrent(session.save(input));
  }, []);

  const signOut = useCallback(() => {
    session.clear();
    setCurrent(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      email: current?.email ?? null,
      isLoading: false,
      isAuthenticated: current !== null,
      signIn,
      signOut,
    }),
    [current, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 는 AuthProvider 안에서만 쓸 수 있어요.");
  return ctx;
}
